import {
  ChangeEvent,
  ChangeEventHandler,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useRef,
} from 'react';
import { PhoneVerificationForm } from 'pages/PhoneVerification/PhoneVerification';
import classes from 'classnames';
import styles from './DigitalCodeInput.module.scss';

export type DigitalCodeInputProps = {
  inputClassName?: string;
  values: PhoneVerificationForm;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  dataTest?: string;
  name: string;
};

export default function DigitalCodeInput({
  inputClassName,
  values,
  onChange,
  name,
}: DigitalCodeInputProps) {
  const fields = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.preventDefault();

      // @ts-ignore
      const prevValue = values[event.target.name];
      const nextValue = event.target.value.replace(prevValue, '');

      event.target.value = nextValue !== undefined ? nextValue : '';

      const index = Number(event.target.id);

      // Navigate to next input
      if (event.target.value) {
        fields.current[index + 1]?.focus();
      } else {
        fields.current[index - 1]?.focus();
      }

      if (onChange) {
        onChange(event);
      }
    },
    [values, onChange, fields],
  );

  const handleKeyUp = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    const index = Number(event.currentTarget.id);
    const isBackspace = event.key === 'Backspace';
    const isRightArrow = event.key === 'ArrowRight';
    const isLeftArrow = event.key === 'ArrowLeft';

    // Navigate input fields with keys
    if ((isBackspace && !event.currentTarget.value) || isLeftArrow) {
      fields.current[index - 1]?.focus();
    } else if (isRightArrow) {
      fields.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const ONLY_NUMBER = /^\d+$/;

      if (!ONLY_NUMBER.test(event.key)) {
        // Allow only digit inputs
        event.preventDefault();
      }
    },
    [],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();
      const ONLY_NUMBER = /^\d+$/;
      const paste: string[] = event.clipboardData
        .getData('text')
        .split('')
        .splice(0, Object.keys(values).length);

      const hasNonValidChar = paste.some((char) => !ONLY_NUMBER.test(char));

      if (hasNonValidChar) {
        return;
      }

      /* if (onChange) {
        onChange(name, paste);
      } */

      if (paste.length >= Object.keys(values).length) {
        // All inputs are full, nowhere to navigate anymore
        fields.current[paste.length - 1]?.focus();
      } else {
        // Go to next input if available
        fields.current[paste.length]?.focus();
      }
    },
    [values],
  );

  return (
    <>
      {values &&
        Object.keys(values).map((key, index) => {
          return (
            <input
              key={index}
              id={index.toString()}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : undefined}
              className={classes([
                styles.border,
                styles['digital-code-input'],
                inputClassName,
              ])}
              data-test={`input-${index}`}
              name={key}
              // @ts-ignore
              value={values[key] || ''}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              onKeyPress={handleKeyPress}
              onPaste={handlePaste}
              ref={(node) => (fields.current[index] = node)}
            />
          );
        })}
    </>
  );
}
